/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useState, useEffect } from 'react';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { Client } from 'api/models/response/ClientList';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DropdownPrompts } from '../../../ClientView.styled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../../../../SearchBars.styled';

type SelectClientSearchBarProps = {
    mobile?: boolean;
    currentClient: Client;
    onboarding?: boolean;
    matchingWidth?: string;
};

const SelectClientSearchBar = ({
    mobile,
    currentClient,
    onboarding,
    matchingWidth,
}: SelectClientSearchBarProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const params = useParams();
    const [searchInput, setSearchInput] = useState<string>('');
    const [matchingClients, setMatchingClients] = useState<Client[]>([]);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(5);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loading, setLoading] = useState(false);
    const [resetState, setResetState] = useState(false);
    const [delayedReset, setDelayedReset] = useState(false);
    const isOnboarding = params['*']?.includes('sales-onboarding');
    const isClientView = params['*']?.includes('client-profile');
    const isRetainerPage = params['*']?.includes('retainer');
    const searchContainerElement = document.getElementById('search-container');
    let widthOfSearchContainer = '';

    if (searchContainerElement !== null) {
        widthOfSearchContainer = window
            .getComputedStyle(searchContainerElement)
            .getPropertyValue('width');
    }

    const height =
        matchingClients.length >= 5
            ? 170
            : matchingClients.length === 4
              ? 150
              : matchingClients.length === 3
                ? 120
                : matchingClients.length === 2
                  ? 80
                  : 30;

    const handleSearchBarClick = () => {
        fetchMatchingClients(pageSize, searchInput);
    };

    const handleChange = (e: any) => {
        e.preventDefault();
        setSearchInput(e.target.value);
        setResetState(true);
        fetchMatchingClients(pageSize, e.target.value);
    };

    const handleBlur = () => {
        setDelayedReset(true);
        setSearchInput('');
    };

    const handleOptionClick = (id: number) => {
        const currentPath = window.location.pathname;
        const arrayPath = currentPath.split('/');

        let newPath = '';
        if (isOnboarding) {
            if (params['*']?.endsWith('/assessment') || params['*']?.endsWith('/agreement')) {
                newPath = currentPath + `/${id.toString()}`;
            } else if (arrayPath.length === 5) {
                arrayPath[4] = id.toString();
                newPath = arrayPath.join('/');
            } else {
                arrayPath[4] = id.toString();
                arrayPath.pop();
                arrayPath.pop();
                newPath = arrayPath.join('/');
            }
        }
        if (isRetainerPage) {
            navigate(`/dashboard/${id}/client-profile`);
            return;
        } else if (isClientView) {
            const arrayPath = currentPath.split('/');
            arrayPath[2] = id.toString();
            newPath = arrayPath.join('/');
        }
        navigate(`${newPath}`);
    };

    const fetchMatchingClients = async (pageSizeVal: number, input: string) => {
        setLoading(true);
        try {
            const resp = await api.clientList.getClientListBySearchInput(1, pageSizeVal, input);
            if (resp.status === 200 || resp.status === 201) {
                const respHeaders = resp.headers;
                const xPagination = respHeaders['x-pagination'];
                if (xPagination) {
                    const xPaginationObj = JSON.parse(xPagination.replace(/'/g, '"'));
                    setTotalPages(xPaginationObj.TotalPages);
                }
                setMatchingClients([...resp.data]);
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchMoreData = async () => {
        const nextPage = page + 1;
        setPage(nextPage);
        if (nextPage > totalPages) {
            setHasMoreData(false);
        } else {
            setHasMoreData(true);
            try {
                const resp = await api.clientList.getClientListBySearchInput(
                    nextPage,
                    pageSize,
                    searchInput
                );
                if (resp.status === 200 || resp.status === 201) {
                    const respHeaders = resp.headers;
                    const xPagination = respHeaders['x-pagination'];
                    if (xPagination) {
                        const xPaginationObj = JSON.parse(xPagination.replace(/'/g, '"'));
                        setTotalPages(xPaginationObj.TotalPages);
                    }
                    setMatchingClients([...matchingClients, ...resp.data]);
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    useEffect(() => {
        if (resetState && !delayedReset) {
            setMatchingClients([]);
            setTotalPages(1);
            setPage(1);
            setHasMoreData(true);
            setResetState(false);
        }
    }, [resetState, delayedReset]);

    useEffect(() => {
        if (delayedReset) {
            setResetState(true);
            const delayTimeout = setTimeout(() => {
                setDelayedReset(false);
            }, 1000);

            return () => clearTimeout(delayTimeout);
        }
    }, [delayedReset]);

    return (
        <S.RelativeContainer
            style={
                matchingWidth
                    ? { minWidth: matchingWidth }
                    : isClientView || isOnboarding
                      ? { width: '100%' }
                      : {}
            }
        >
            <S.SearchContainer
                id="search-container"
                clientView={isClientView}
                mobile={mobile}
                onboarding={onboarding}
            >
                <S.SearchInput
                    type="text"
                    placeholder={`${currentClient?.firstname} ${currentClient?.lastname}`}
                    onChange={handleChange}
                    onClick={handleSearchBarClick}
                    onBlur={handleBlur}
                    value={searchInput}
                    clientView={true}
                />

                <SearchIcon style={{ width: '14px' }} />
            </S.SearchContainer>
            <S.AbsoluteContainer>
                {matchingClients.length > 0 ? (
                    <S.MatchingData
                        style={{
                            minWidth: widthOfSearchContainer,
                            maxWidth: widthOfSearchContainer,
                        }}
                        clientView={isClientView}
                        mobile={mobile}
                    >
                        <InfiniteScroll
                            dataLength={matchingClients.length}
                            next={fetchMoreData}
                            hasMore={hasMoreData}
                            loader={
                                <DropdownPrompts>{`${t('loading', {
                                    lng: language,
                                })}...`}</DropdownPrompts>
                            }
                            height={height}
                            endMessage={
                                <DropdownPrompts>{`- ${t('endOfList', {
                                    lng: language,
                                })} -`}</DropdownPrompts>
                            }
                        >
                            {matchingClients.map((option) => {
                                return (
                                    <S.Option
                                        key={option.id}
                                        onClick={() => handleOptionClick(option.id)}
                                    >
                                        <S.Name>{`${option.firstname} ${option.lastname}`}</S.Name>
                                    </S.Option>
                                );
                            })}
                            {hasMoreData && (
                                <DropdownPrompts>{`- ${t('scrollForMore', {
                                    lng: language,
                                })} -`}</DropdownPrompts>
                            )}
                        </InfiniteScroll>
                    </S.MatchingData>
                ) : (
                    <>
                        {!loading && searchInput.length > 0 && (
                            <S.MatchingData
                                style={{
                                    minWidth: widthOfSearchContainer,
                                    maxWidth: widthOfSearchContainer,
                                }}
                                clientView={isClientView}
                                mobile={mobile}
                            >
                                <DropdownPrompts>{`- ${t('noMatchingClients', {
                                    lng: language,
                                })} -`}</DropdownPrompts>
                            </S.MatchingData>
                        )}
                    </>
                )}
            </S.AbsoluteContainer>
        </S.RelativeContainer>
    );
};

export default SelectClientSearchBar;
